import React from 'react';
import { CheckCircle2, Clock, Truck, RefreshCw, Package, ExternalLink, type LucideIcon } from 'lucide-react';
import type { OrderCardData } from '../../utils/orderBlocks';

interface OrderCardProps {
  data: OrderCardData;
  brandColor: string;
  isDark: boolean;
}

function formatRelativeDate(iso: string): string {
  if (!iso) return '';
  const then = new Date(iso);
  if (Number.isNaN(then.getTime())) return '';
  const now = new Date();
  const diffMs = now.getTime() - then.getTime();
  const diffMin = Math.floor(diffMs / 60000);
  const diffHr = Math.floor(diffMin / 60);
  const diffDay = Math.floor(diffHr / 24);

  if (diffMin < 1) return 'just now';
  if (diffMin < 60) return `${diffMin} minute${diffMin === 1 ? '' : 's'} ago`;
  if (diffHr < 24) return `${diffHr} hour${diffHr === 1 ? '' : 's'} ago`;
  if (diffDay === 1) return 'Yesterday';
  if (diffDay < 7) return `${diffDay} days ago`;
  return then.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

function formatTotal(total: string, currency: string): string {
  const num = Number(total);
  if (Number.isFinite(num)) {
    // Connector orders may omit currency — show the bare amount rather
    // than assuming USD. Shopify always sends currency, path unchanged.
    if (!currency) return String(total);
    try {
      return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: currency || 'USD',
        currencyDisplay: 'symbol',
      }).format(num) + ` ${currency || 'USD'}`;
    } catch {
      return `$${total} ${currency || 'USD'}`;
    }
  }
  return `${total} ${currency || ''}`.trim();
}

function formatPrice(price: string, currency: string): string {
  if (price == null || price === '') return '';
  const num = Number(price);
  if (Number.isFinite(num)) {
    if (!currency) return String(price);
    try {
      return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: currency || 'USD',
      }).format(num);
    } catch {
      return `$${price}`;
    }
  }
  return price;
}

type PillTone = 'green' | 'amber' | 'red' | 'gray';

const TONE_LIGHT: Record<PillTone, { bg: string; fg: string }> = {
  green: { bg: 'rgba(34,197,94,0.10)', fg: '#15803d' },
  amber: { bg: 'rgba(245,158,11,0.12)', fg: '#b45309' },
  red:   { bg: 'rgba(239,68,68,0.10)',  fg: '#b91c1c' },
  gray:  { bg: 'rgba(107,114,128,0.12)', fg: '#374151' },
};

const TONE_DARK: Record<PillTone, { bg: string; fg: string }> = {
  green: { bg: 'rgba(34,197,94,0.18)', fg: '#86efac' },
  amber: { bg: 'rgba(245,158,11,0.22)', fg: '#fcd34d' },
  red:   { bg: 'rgba(239,68,68,0.18)',  fg: '#fca5a5' },
  gray:  { bg: 'rgba(156,163,175,0.20)', fg: '#d1d5db' },
};

function financialMeta(status: string): { tone: PillTone; label: string; Icon: LucideIcon } {
  switch ((status || '').toLowerCase()) {
    case 'paid':                return { tone: 'green', label: 'Paid',                Icon: CheckCircle2 };
    case 'pending':             return { tone: 'amber', label: 'Pending',             Icon: Clock };
    case 'refunded':            return { tone: 'red',   label: 'Refunded',            Icon: RefreshCw };
    case 'partially_refunded':  return { tone: 'red',   label: 'Partially refunded',  Icon: RefreshCw };
    default:                    return { tone: 'gray',  label: status || 'Unknown',   Icon: Clock };
  }
}

function fulfillmentMeta(status: string): { tone: PillTone; label: string; Icon: LucideIcon } {
  switch ((status || '').toLowerCase()) {
    case 'fulfilled':   return { tone: 'green', label: 'Fulfilled',   Icon: Truck };
    case 'partial':     return { tone: 'amber', label: 'Partial',     Icon: Truck };
    case 'unfulfilled': return { tone: 'gray',  label: 'Unfulfilled', Icon: Package };
    default:            return { tone: 'gray',  label: status || 'Unknown', Icon: Package };
  }
}

const OrderCard: React.FC<OrderCardProps> = ({ data, brandColor, isDark }) => {
  const cardBg = isDark ? '#1f2937' : '#ffffff';
  const borderColor = isDark ? '#374151' : '#e5e7eb';
  const dividerColor = isDark ? '#374151' : '#e5e7eb';
  const titleColor = isDark ? '#ffffff' : '#111827';
  const subColor = isDark ? '#9ca3af' : '#6b7280';
  const bodyColor = isDark ? '#e5e7eb' : '#1f2937';
  const thumbBg = isDark ? '#111827' : '#f3f4f6';
  const tones = isDark ? TONE_DARK : TONE_LIGHT;

  const fin = financialMeta(data.financial_status);
  const ful = fulfillmentMeta(data.fulfillment_status);
  const items = Array.isArray(data.line_items) ? data.line_items : [];
  const visibleItems = items.slice(0, 3);
  const extraCount = Math.max(0, items.length - visibleItems.length);
  const relDate = formatRelativeDate(data.created_at);
  const finPill = tones[fin.tone];
  const fulPill = tones[ful.tone];

  return (
    <div
      style={{
        background: cardBg,
        border: `1px solid ${borderColor}`,
        borderLeft: `4px solid ${brandColor}`,
        borderRadius: '12px',
        overflow: 'hidden',
        margin: '8px 0',
        padding: '12px',
        display: 'flex',
        flexDirection: 'column',
        gap: '10px',
        boxShadow: isDark ? 'none' : '0 1px 2px rgba(0,0,0,0.04)',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: '8px', flexWrap: 'wrap' }}>
        <div style={{ fontSize: '14px', fontWeight: 700, color: titleColor }}>
          {data.number ? `Order #${data.number}` : 'Order'}
        </div>
        {relDate && (
          <div style={{ fontSize: '11px', color: subColor }}>{relDate}</div>
        )}
      </div>

      {/* Connector orders may omit either status — the pill hides instead
          of showing an "Unknown" placeholder. Shopify sends both, unchanged. */}
      {(data.financial_status || data.fulfillment_status) && (
        <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
          {data.financial_status && (
            <span
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '4px',
                fontSize: '11px',
                fontWeight: 600,
                color: finPill.fg,
                background: finPill.bg,
                padding: '3px 8px',
                borderRadius: '999px',
              }}
            >
              <fin.Icon size={12} />
              {fin.label}
            </span>
          )}
          {data.fulfillment_status && (
            <span
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '4px',
                fontSize: '11px',
                fontWeight: 600,
                color: fulPill.fg,
                background: fulPill.bg,
                padding: '3px 8px',
                borderRadius: '999px',
              }}
            >
              <ful.Icon size={12} />
              {ful.label}
            </span>
          )}
        </div>
      )}

      {visibleItems.length > 0 && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
          {visibleItems.map((item, i) => (
            <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '10px', minWidth: 0 }}>
              <div
                style={{
                  width: '40px',
                  height: '40px',
                  flexShrink: 0,
                  background: thumbBg,
                  borderRadius: '6px',
                  overflow: 'hidden',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: subColor,
                }}
              >
                {item.image_url ? (
                  <img
                    src={item.image_url}
                    alt={item.title}
                    style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
                  />
                ) : (
                  <Package size={18} />
                )}
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div
                  style={{
                    fontSize: '12px',
                    fontWeight: 500,
                    color: bodyColor,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {item.title}
                </div>
                {item.qty > 1 && (
                  <div style={{ fontSize: '11px', color: subColor }}>x{item.qty}</div>
                )}
              </div>
              {item.price != null && item.price !== '' && (
                <div style={{ fontSize: '12px', fontWeight: 600, color: bodyColor, flexShrink: 0 }}>
                  {formatPrice(item.price, data.currency)}
                </div>
              )}
            </div>
          ))}
          {extraCount > 0 && (
            <div style={{ fontSize: '11px', color: subColor, paddingLeft: '50px' }}>
              +{extraCount} more item{extraCount === 1 ? '' : 's'}
            </div>
          )}
        </div>
      )}

      {data.total != null && data.total !== '' && (
        <div style={{ borderTop: `1px solid ${dividerColor}`, paddingTop: '8px', display: 'flex', justifyContent: 'flex-end' }}>
          <div style={{ fontSize: '13px', fontWeight: 700, color: titleColor }}>
            Total: {formatTotal(data.total, data.currency)}
          </div>
        </div>
      )}

      {/* Alphanumeric codes — voice would garble these, so they render
          visually. Absent on Shopify + existing cards → byte-identical. */}
      {(data.tracking || data.code) && (
        <div
          style={{
            borderTop: `1px solid ${dividerColor}`,
            paddingTop: '8px',
            display: 'flex',
            flexDirection: 'column',
            gap: '6px',
          }}
        >
          {data.tracking && (
            <div style={{ display: 'flex', gap: '6px', fontSize: '12px', alignItems: 'baseline', flexWrap: 'wrap' }}>
              <span style={{ color: subColor, flexShrink: 0 }}>Tracking:</span>
              {data.tracking_url ? (
                <a
                  href={data.tracking_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{
                    fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Consolas, monospace',
                    color: brandColor,
                    textDecoration: 'underline',
                    overflowWrap: 'anywhere',
                    minWidth: 0,
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '4px',
                  }}
                >
                  {data.tracking}
                  <ExternalLink size={11} style={{ flexShrink: 0 }} />
                </a>
              ) : (
                <span
                  style={{
                    fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Consolas, monospace',
                    color: bodyColor,
                    overflowWrap: 'anywhere',
                    minWidth: 0,
                    userSelect: 'all',
                  }}
                >
                  {data.tracking}
                </span>
              )}
            </div>
          )}
          {data.code && (
            <div style={{ display: 'flex', gap: '6px', fontSize: '12px', alignItems: 'baseline', flexWrap: 'wrap' }}>
              <span style={{ color: subColor, flexShrink: 0 }}>Code:</span>
              <span
                style={{
                  fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Consolas, monospace',
                  color: bodyColor,
                  overflowWrap: 'anywhere',
                  minWidth: 0,
                  userSelect: 'all',
                }}
              >
                {data.code}
              </span>
            </div>
          )}
        </div>
      )}

      {data.order_status_url && (
        <a
          href={data.order_status_url}
          target="_blank"
          rel="noopener noreferrer"
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '6px',
            fontSize: '12px',
            fontWeight: 600,
            color: brandColor,
            border: `1px solid ${brandColor}`,
            background: 'transparent',
            padding: '6px 12px',
            borderRadius: '8px',
            textDecoration: 'none',
          }}
        >
          View order status
          <ExternalLink size={12} />
        </a>
      )}
    </div>
  );
};

export default OrderCard;
